/**
 * DocumentDB API Service
 *
 * API methods for document database operations including creation, updates,
 * and access grants. DocumentDB provides JSON document storage for applications.
 * Updated to use standardized API response format.
 */

import apiClient from "./client";
import type { ApiSuccessResponse, ApiCollectionResponse, ApiActionResponse } from "../types/api.types";
import type {
  DocumentDatabaseSanitized,
  DocumentDatabaseCreateDTO,
  DocumentDatabaseUpdateDTO,
  DocumentDatabaseListQuery,
} from "../types/document-database.types";

// ============================================================================
// DOCUMENT DATABASE SERVICE
// ============================================================================

export const documentDatabaseService = {
  /**
   * List all document databases for a tenant
   */
  listDocumentDatabases: async (
    tenantId: string,
    query?: DocumentDatabaseListQuery
  ): Promise<DocumentDatabaseSanitized[]> => {
    const response = await apiClient.get<ApiCollectionResponse<DocumentDatabaseSanitized>>(
      `/tenants/${tenantId}/document-dbs`,
      { params: query }
    );
    return response.data.data;
  },

  /**
   * Get document database by ID
   */
  getDocumentDatabase: async (
    tenantId: string,
    documentDBId: string
  ): Promise<DocumentDatabaseSanitized> => {
    const response = await apiClient.get<ApiSuccessResponse<DocumentDatabaseSanitized>>(
      `/tenants/${tenantId}/document-dbs/${documentDBId}`
    );
    return response.data.data;
  },

  /**
   * Create a new document database
   */
  createDocumentDatabase: async (
    tenantId: string,
    data: DocumentDatabaseCreateDTO
  ): Promise<DocumentDatabaseSanitized> => {
    const response = await apiClient.post<ApiSuccessResponse<DocumentDatabaseSanitized>>(
      `/tenants/${tenantId}/document-dbs`,
      data
    );
    return response.data.data;
  },

  /**
   * Update document database
   */
  updateDocumentDatabase: async (
    tenantId: string,
    documentDBId: string,
    data: DocumentDatabaseUpdateDTO
  ): Promise<DocumentDatabaseSanitized> => {
    const response = await apiClient.put<ApiSuccessResponse<DocumentDatabaseSanitized>>(
      `/tenants/${tenantId}/document-dbs/${documentDBId}`,
      data
    );
    return response.data.data;
  },

  /**
   * Delete document database
   */
  deleteDocumentDatabase: async (
    tenantId: string,
    documentDBId: string
  ): Promise<{ message: string }> => {
    const response = await apiClient.delete<ApiActionResponse>(
      `/tenants/${tenantId}/document-dbs/${documentDBId}`
    );
    return { message: response.data.message };
  },
};

export default documentDatabaseService;
